import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, LogOut, ExternalLink } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { http } from "../components/resources/resourceUtils";
import { LoginForm, ForgotForm, ResetForm } from "../components/admin-resources/AdminAuthForms";
import { ResourceTable } from "../components/admin-resources/ResourceTable";
import { ResourceFormDialog } from "../components/admin-resources/ResourceFormDialog";
import { CategoryManager } from "../components/admin-resources/CategoryManager";
import { SettingsPanel } from "../components/admin-resources/SettingsPanel";
import "../styles/resources.css";

const PING_MS = 10 * 60 * 1000;

// Keeps the 8 hour inactivity window sliding while the admin is active.
function useSessionKeepAlive(active, onExpired) {
    const lastActivity = useRef(Date.now());
    useEffect(() => {
        if (!active) return undefined;
        const mark = () => { lastActivity.current = Date.now(); };
        const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
        events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
        const timer = setInterval(() => {
            if (Date.now() - lastActivity.current < PING_MS) http.get("/admin-auth/me").catch(() => onExpired());
        }, PING_MS);
        return () => { events.forEach((e) => window.removeEventListener(e, mark)); clearInterval(timer); };
    }, [active, onExpired]);
}

const Dashboard = ({ email, onLogout, onExpired }) => {
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(null);
    const load = useCallback(async () => {
        try { setData((await http.get("/admin/resources")).data); } catch (e) { if (e?.response?.status === 401) onExpired(); }
    }, [onExpired]);
    useEffect(() => { load(); }, [load]);
    const catName = useMemo(() => Object.fromEntries((data?.categories || []).map((c) => [c.id, c.name])), [data]);
    if (!data) return <p className="py-20 text-center" data-testid="admin-loading">Loading...</p>;
    const rows = data.resources.filter((r) => filter === "all" || r.category_id === filter);

    return (
        <div className="mx-auto max-w-[1200px] px-5 lg:px-10 py-12 lg:py-16" data-testid="admin-dashboard">
            <header className="flex flex-wrap items-end justify-between gap-6">
                <div>
                    <span className="res-eyebrow">Alchemy Practice</span>
                    <h1 className="mt-2 font-serif text-[40px] lg:text-[48px] leading-tight" style={{ color: "#4A4A30" }}>Resources admin</h1>
                    <p className="text-[15px] mt-1" style={{ color: "#7D7D68" }} data-testid="admin-signed-in-as">Signed in as {email}</p>
                </div>
                <div className="flex items-center gap-4">
                    <a href="/resources" target="_blank" rel="noopener noreferrer" className="res-link inline-flex items-center gap-1.5" data-testid="admin-view-page"><ExternalLink size={15} /> View page</a>
                    <button type="button" onClick={onLogout} className="res-link inline-flex items-center gap-1.5" data-testid="admin-logout"><LogOut size={15} /> Sign out</button>
                </div>
            </header>
            <Tabs defaultValue="resources" className="mt-10">
                <TabsList className="bg-[#F1EFE8] rounded-full p-1 h-auto">
                    {["resources", "categories", "settings"].map((t) => (
                        <TabsTrigger key={t} value={t} className="rounded-full px-5 py-2 text-[15px] capitalize data-[state=active]:bg-white" data-testid={`admin-tab-${t}`}>{t}</TabsTrigger>
                    ))}
                </TabsList>
                <TabsContent value="resources" className="mt-8">
                    <div className="res-card p-5 lg:p-7">
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                            <Select value={filter} onValueChange={setFilter}>
                                <SelectTrigger className="w-[240px] rounded-xl h-11 text-[15px]" aria-label="Filter by category" data-testid="admin-filter-category"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All categories</SelectItem>
                                    {data.categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                            <button type="button" className="res-btn" onClick={() => setEditing({})} data-testid="admin-add-resource"><Plus size={16} /> Add resource</button>
                        </div>
                        <p className="text-[14px] mb-3" style={{ color: "#7D7D68" }}>Drag rows or use the arrows to set the order. Choose a category above to reorder within it.</p>
                        <ResourceTable rows={rows} catName={catName} onEdit={setEditing} onChanged={load} />
                    </div>
                </TabsContent>
                <TabsContent value="categories" className="mt-8">
                    <div className="res-card p-7 lg:p-8"><CategoryManager categories={data.categories} resources={data.resources} onChanged={load} /></div>
                </TabsContent>
                <TabsContent value="settings" className="mt-8">
                    <SettingsPanel settings={data.settings} onChanged={load} />
                </TabsContent>
            </Tabs>
            <ResourceFormDialog open={!!editing} resource={editing?.id ? editing : null} categories={data.categories}
                onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
        </div>
    );
};

export default function AdminResourcesPage() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token");
    const [admin, setAdmin] = useState(undefined);
    const [view, setView] = useState("login");
    const [notice, setNotice] = useState("");

    useEffect(() => {
        http.get("/admin-auth/me").then(({ data }) => setAdmin(data)).catch(() => setAdmin(null));
    }, []);
    const expire = useCallback(() => { setAdmin(null); setNotice("Your session timed out. Please sign in again."); }, []);
    useSessionKeepAlive(!!admin, expire);
    const logout = async () => { await http.post("/admin-auth/logout").catch(() => {}); setAdmin(null); setNotice(""); };

    let body;
    if (token) {
        body = <ResetForm token={token} onDone={() => { setNotice("Password updated. Please sign in."); navigate("/admin/resources", { replace: true }); }} />;
    } else if (admin === undefined) {
        body = <p className="py-20 text-center" data-testid="admin-checking">Loading...</p>;
    } else if (admin) {
        body = <Dashboard email={admin.email} onLogout={logout} onExpired={expire} />;
    } else if (view === "forgot") {
        body = <ForgotForm onBack={() => setView("login")} />;
    } else {
        body = <LoginForm notice={notice} onLoggedIn={(d) => { setNotice(""); setAdmin(d); }} onForgot={() => setView("forgot")} />;
    }

    return (
        <main className="ap-res min-h-screen" data-testid="admin-resources-page">
            <Helmet>
                <title>Resources admin | Alchemy Practice</title>
                <meta name="robots" content="noindex, nofollow, noarchive" />
            </Helmet>
            {admin ? body : <div className="px-5 py-24 lg:py-32">{body}</div>}
        </main>
    );
}
